/**
 * XP, levels and ranks — all derived, none stored.
 */

import { read, type Counters } from './counters';

/** What each unit of a counter is worth. A metric left out is worth nothing. */
export type XpWeights<M extends string> = Readonly<Partial<Record<M, number>>>;

export function xpFrom<M extends string>(counters: Counters<M>, weights: XpWeights<M>): number {
  let total = 0;
  for (const metric of Object.keys(weights) as M[]) {
    const weight = weights[metric] ?? 0;
    if (!(weight > 0)) continue;
    total += read(counters, metric) * weight;
  }
  return Math.floor(total);
}

/**
 * A triangular curve: reaching level `L` costs `step × L(L−1)/2` XP in total.
 *
 * With `step = 100` that is 100 for level 2, 300 for 3, 1 000 for 5, 4 500
 * for 10. Each level costs `step` more than the one before, which is gentle
 * enough that the first levels arrive inside a first session — the moment a
 * new player decides whether this is worth coming back to — and steep enough
 * that the late ones mean something.
 */
export interface LevelCurve {
  step: number;
  maxLevel: number;
}

export function xpToReach(level: number, curve: LevelCurve): number {
  const l = Math.max(1, Math.min(Math.floor(level), curve.maxLevel));
  return (curve.step * l * (l - 1)) / 2;
}

export interface LevelInfo {
  level: number;
  xp: number;
  /** Total XP at which this level began. */
  floor: number;
  /** Total XP needed for the next level, or null at the cap. */
  next: number | null;
  /** 0–1 through the current level; 1 at the cap. */
  progress: number;
}

export function levelFor(xp: number, curve: LevelCurve): LevelInfo {
  const safe = Number.isFinite(xp) && xp > 0 ? Math.floor(xp) : 0;
  // Closed-form inverse of the triangle, then nudged: the square root is
  // floating point and can land one either side of an exact boundary.
  let level = Math.floor((1 + Math.sqrt(1 + (8 * safe) / curve.step)) / 2);
  level = Math.max(1, Math.min(level, curve.maxLevel));
  while (level < curve.maxLevel && xpToReach(level + 1, curve) <= safe) level += 1;
  while (level > 1 && xpToReach(level, curve) > safe) level -= 1;

  const floor = xpToReach(level, curve);
  if (level >= curve.maxLevel) {
    return { level, xp: safe, floor, next: null, progress: 1 };
  }
  const next = xpToReach(level + 1, curve);
  return { level, xp: safe, floor, next, progress: (safe - floor) / (next - floor) };
}

/** A title held from `from` until the next rank's `from`. */
export interface Rank {
  from: number;
  name: string;
  nameAr: string;
}

/** Ranks must be sorted by `from`; the first should start at level 1. */
export function rankFor(level: number, ranks: readonly Rank[]): Rank {
  let current = ranks[0];
  for (const rank of ranks) {
    if (rank.from <= level) current = rank;
  }
  return current;
}
